import { getCached, setCache } from './cache';
import { hubspotGet } from './hubspotClient';
import { HUBSPOT_API } from '../constants';
import type {
  FormPerformanceData,
  HSFormsResponse,
  HSFormSubmissionsResponse,
  EmailMarketingData,
  HSCampaignDetail,
  HSCampaignsListResponse,
} from '../types/hubspot';

const CACHE_TTL = HUBSPOT_API.CACHE_TTL_MS;

// ── Form Performance ──

export async function fetchFormPerformance(signal?: AbortSignal): Promise<FormPerformanceData> {
  const cacheKey = 'hs:forms';
  const cached = getCached<FormPerformanceData>(cacheKey, CACHE_TTL);
  if (cached) return cached;

  const formsRes = await hubspotGet<HSFormsResponse>(
    'marketing/v3/forms',
    { limit: '100' },
    signal
  );

  const forms = formsRes.results || [];

  // Fetch submissions sequentially in batches of 5 to avoid rate limits
  const formRows: FormRow[] = [];
  const recentSubmissions: RecentSubmission[] = [];
  const topForms = forms.slice(0, 20);

  for (let i = 0; i < topForms.length; i += 5) {
    const batch = topForms.slice(i, i + 5);
    const batchResults = await Promise.allSettled(
      batch.map(async (form) => {
        const subsRes = await hubspotGet<HSFormSubmissionsResponse>(
          `form-integrations/v1/submissions/forms/${form.id}`,
          { limit: '50' },
          signal
        );
        return { form, subs: subsRes.results || [] };
      })
    );

    for (const result of batchResults) {
      if (result.status === 'fulfilled') {
        const { form, subs } = result.value;
        const lastSub = subs.length > 0 ? new Date(subs[0].submittedAt).toISOString() : null;

        formRows.push({
          id: form.id,
          name: form.name,
          submissions: subs.length,
          lastSubmitted: lastSub,
        });

        for (const sub of subs.slice(0, 5)) {
          const emailField = sub.values.find(
            (v) => v.name === 'email' || v.name === 'Email'
          );
          recentSubmissions.push({
            formName: form.name,
            submittedAt: new Date(sub.submittedAt).toISOString(),
            email: emailField?.value || 'N/A',
          });
        }
      } else {
        const form = batch[batchResults.indexOf(result)];
        formRows.push({ id: form.id, name: form.name, submissions: 0, lastSubmitted: null });
      }
    }
  }

  // Add remaining forms with 0 submissions
  for (const form of forms.slice(20)) {
    formRows.push({ id: form.id, name: form.name, submissions: 0, lastSubmitted: null });
  }

  formRows.sort((a, b) => b.submissions - a.submissions);
  recentSubmissions.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());

  const topFormsBars = formRows
    .filter((f) => f.submissions > 0)
    .slice(0, 10)
    .map((f) => ({ name: f.name, submissions: f.submissions }));

  const result: FormPerformanceData = {
    forms: formRows,
    topForms: topFormsBars,
    recentSubmissions: recentSubmissions.slice(0, 20),
  };
  setCache(cacheKey, result);
  return result;
}

// ── Email Marketing ──
// Uses v1 campaigns API directly — each campaign has name, subject, and counters

export async function fetchEmailMarketing(signal?: AbortSignal): Promise<EmailMarketingData> {
  const cacheKey = 'hs:emails';
  const cached = getCached<EmailMarketingData>(cacheKey, CACHE_TTL);
  if (cached) return cached;

  // Get recent campaign IDs (sorted by lastUpdatedTime desc)
  const campaignsRes = await hubspotGet<HSCampaignsListResponse>(
    'email/public/v1/campaigns',
    { limit: '100' },
    signal
  );

  const campaignIds = (campaignsRes.campaigns || []).map((c) => c.id);

  // Fetch campaign details in batches of 5
  const allDetails: HSCampaignDetail[] = [];
  for (let i = 0; i < campaignIds.length; i += 5) {
    const batch = campaignIds.slice(i, i + 5);
    const batchResults = await Promise.allSettled(
      batch.map((id) =>
        hubspotGet<HSCampaignDetail>(`email/public/v1/campaigns/${id}`, undefined, signal)
      )
    );
    for (const result of batchResults) {
      if (result.status === 'fulfilled') {
        allDetails.push(result.value);
      }
    }
  }

  let totalSent = 0;
  let totalOpens = 0;
  let totalClicks = 0;
  let totalBounces = 0;
  let totalDelivered = 0;

  const campaigns = allDetails.map((detail) => {
    const counters = detail.counters || {};
    const sent = counters.sent || 0;
    const delivered = counters.delivered || 0;
    const opens = counters.open || 0;
    const clicks = counters.click || 0;
    const bounces = counters.bounce || 0;

    const openRate = delivered > 0 ? opens / delivered : 0;
    const clickRate = delivered > 0 ? clicks / delivered : 0;
    const bounceRate = sent > 0 ? bounces / sent : 0;

    totalSent += sent;
    totalOpens += opens;
    totalClicks += clicks;
    totalBounces += bounces;
    totalDelivered += delivered;

    // Use lastProcessingFinishedAt or scheduledAt for sent date
    const sentTimestamp = detail.lastProcessingFinishedAt || detail.scheduledAt;
    const sentDate = sentTimestamp ? new Date(sentTimestamp).toISOString() : '';

    return {
      id: String(detail.id),
      name: detail.name || '',
      subject: detail.subject || '',
      sentDate,
      sent,
      openRate,
      clickRate,
      bounceRate,
    };
  });

  // Sort by sent date descending
  campaigns.sort((a, b) => new Date(b.sentDate).getTime() - new Date(a.sentDate).getTime());

  const avgOpenRate = totalDelivered > 0 ? totalOpens / totalDelivered : 0;
  const avgClickRate = totalDelivered > 0 ? totalClicks / totalDelivered : 0;
  const avgBounceRate = totalSent > 0 ? totalBounces / totalSent : 0;

  const result: EmailMarketingData = {
    totalSent,
    avgOpenRate,
    avgClickRate,
    avgBounceRate,
    campaigns,
  };
  setCache(cacheKey, result);
  return result;
}
